import { motion } from "framer-motion";
import { useEnquiry } from "@/components/site/EnquiryProvider";
import { usePayment } from "@/components/site/PaymentProvider";
import { ArrowUpRight } from "lucide-react";

export default function ArtworkCard({ art, index, aspect = "aspect-[4/5]" }) {
  const { openEnquiry } = useEnquiry();
  const { buyArtwork } = usePayment();
  const purchasable = !art.price_on_request;

  const handleBuy = (e) => {
    e.stopPropagation();
    buyArtwork(art, { onEnquireFallback: () => openEnquiry(art) });
  };
  const handleEnquire = (e) => {
    e.stopPropagation();
    openEnquiry(art);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group cursor-pointer"
      onClick={() => openEnquiry(art)}
      data-testid={`artwork-card-${art.slug}`}
      data-cursor="Enquire"
    >
      <div className={`relative overflow-hidden bg-sand gallery-shadow ${aspect}`}>
        <img
          src={art.image}
          alt={art.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/15" />
        <div className="absolute top-4 right-4 h-11 w-11 flex items-center justify-center bg-paper text-ink translate-y-3 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
          <ArrowUpRight className="h-5 w-5" />
        </div>
        {art.kind === "photograph" && (
          <span className="absolute top-4 left-4 bg-ink/80 text-paper font-body text-[9px] tracking-[0.2em] uppercase px-2.5 py-1">
            Photograph
          </span>
        )}
      </div>

      <div className="pt-5 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight tracking-tight group-hover:text-ochre transition-colors">
            {art.title}
          </h3>
          <p className="font-body text-[11px] tracking-[0.18em] uppercase text-ink-soft mt-2">
            {art.medium} &middot; {art.dimensions}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className={`font-body ${art.price_on_request ? "text-xs italic text-ink-soft" : "text-base text-ink"} whitespace-nowrap`}>
            {art.price}
          </p>
          <div className="mt-2 flex items-center justify-end gap-3">
            {purchasable && (
              <button
                type="button"
                onClick={handleBuy}
                data-testid={`artwork-buy-${art.slug}`}
                data-cursor="Buy"
                className="bg-ochre px-3 py-1.5 font-body text-[10px] tracking-[0.18em] uppercase text-paper transition-colors hover:bg-ink"
              >
                Buy now
              </button>
            )}
            <button
              type="button"
              onClick={handleEnquire}
              data-testid={`artwork-enquire-${art.slug}`}
              className="font-body text-[10px] tracking-[0.2em] uppercase text-ochre border-b border-ochre/50 pb-0.5 transition-colors hover:text-ink hover:border-ink"
            >
              Enquire
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
